// Patches the immer instance bundled with this graph (same as @reduxjs/toolkit); see dashboard bootstrap + rspack immer alias.
import '../immer-init';
import history from '../history';
import {
  applyMiddleware,
  compose,
  createStore,
  Middleware,
  Reducer
} from 'redux';
import logger from 'redux-logger';
import { thunk } from 'redux-thunk';
import * as storage from 'redux-storage';
import { reviver } from './session-storage-engine'; //createSessionStorageEngine
import {
  persistSessionState,
  SESSION_STATE_KEY
} from './persist-session-state';
import InitialState from '../reducers/initial-state';
import createRootReducer from '../reducers';
import authMiddleware from '../core/middleware/auth-middleware';
import windowOpenMiddleware from '../core/middleware/window-open-middleware';
import contextMiddleware from '../core/middleware/context-middleware';
import {
  authenticationChannelMiddlewareFactory,
  authenticationChannelReceiverFactory
} from '../core/middleware/authentication-channel-middleware';
import { Auth } from '../actions/action-types';
import { importTableauMiddleware } from '../core/middleware/load-script';
import {
  createReceiverSubscriptions,
  senderSubscriptions
} from '../core/authentication-channel/parent-window-subscriptions';
import { setIsActive } from '../core/session-activity';
import '../core/receivers/post-message-receiver';
import analyticsMiddleware from '../core/middleware/analytics-middleware';
import newDesignMiddleware from '../core/middleware/new-design-middleware';
import bffRoutingMiddleware from '../core/middleware/bff-routing-middleware';
import { serviceDeskMiddleware } from '../core/middleware/servicedesk.middleware';
import stateDisinfectantMiddleware from '../core/middleware/state-disinfectant-middleware';
import {
  AmplifyMiddlewares,
  authenticationChannel,
  authenticationContext,
  TokenRequestChannel,
  navigation,
  objectUtils
} from '@codametrix/ui-common';
import { initClientSessionCsrf, setSessionChannelHandlers } from '@codametrix/client-session';
import {
  syncChildSessionHeartbeatSchedulerAfterAuth,
  syncSessionHeartbeatScheduler
} from '../core/sync-session-heartbeat-scheduler';
import listenerMiddleware from './listener-middleware';
import { withoutTransientState } from './transient-state';

const composeEnhancers =
  (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
// build initial state
let initialState: CMx.ApplicationState = objectUtils.cloneDeep(InitialState);
const serialisedSavedState = window.sessionStorage.getItem(SESSION_STATE_KEY);
window.sessionStorage.removeItem(SESSION_STATE_KEY);

if (serialisedSavedState) {
  try {
    const savedState: CMx.ApplicationState = JSON.parse(
      serialisedSavedState,
      reviver
    );
  // Legacy session payloads may still include `router` from connected-react-router;
    // the root reducer no longer has that slice — strip it so combineReducers does not warn.
    const { router: _legacyRouter, ...savedStateWithoutRouter } =
      savedState as CMx.ApplicationState & { router?: unknown };
    initialState = {
      ...InitialState,
      ...withoutTransientState(savedStateWithoutRouter),
      cases: { ...initialState.cases, ...savedState.cases }
    };
  } catch (err) {
    console.error('Failed to restore session state; starting fresh.', err);
  }
}
const authToken = window.sessionStorage.getItem('authToken'); // sync call - look to make async
window.sessionStorage.removeItem('authToken');
// After a context-switch reload, persisted Redux state carries the rotated jwtToken on
// ui.context, while authToken (written on unload) can lag one rotation behind. Prefer
// the in-state jwt so the next switch revokes the JWT that actually authenticated PUT.
const restoredJwt = initialState.ui?.context?.jwtToken;
const bootstrapToken =
  typeof restoredJwt === 'string' && restoredJwt.length > 0 ? restoredJwt : authToken;
if (bootstrapToken) {
  initialState.login = {
    ...initialState.login,
    auth: { token: bootstrapToken, expiryTimeToLive: null }
  };
}

const restoredActiveContextId = initialState.ui?.context?.activeContextId;
if (restoredActiveContextId != null) {
  authenticationContext.setContextId(restoredActiveContextId);
}

export { history };

let middlewares: Middleware[] = [
  listenerMiddleware.middleware,
  thunk,
  authMiddleware,
  importTableauMiddleware,
  windowOpenMiddleware,
  authenticationChannelMiddlewareFactory(senderSubscriptions),
  analyticsMiddleware,
  newDesignMiddleware,
  bffRoutingMiddleware,
  AmplifyMiddlewares.xcaliberTokenMiddleware,
  serviceDeskMiddleware,
  stateDisinfectantMiddleware,
  contextMiddleware
];

const rootReducer = createRootReducer();
let reducer: Reducer;
let engine: any;

reducer = (state: any, action: any) => {
  if (state && 'router' in state) {
    const { router, ...rest } = state;
    return rootReducer(rest, action);
  }
  return rootReducer(state, action);
};

if (!(process.env.JEST_WORKER_ID || process.env.NODE_ENV === 'production')) {
  middlewares.push(logger as Middleware);
}

// --------------------------------
// store configuration
// ---------------------------------
const store = createStore(
  reducer, // root reducer with router state
  initialState,
  composeEnhancers(applyMiddleware(...middlewares))
);
// once store is created, hook up subscriptions
authenticationChannelReceiverFactory(
  createReceiverSubscriptions(store.dispatch),
  store
);
history.listen((location, action) => {
  store.dispatch({ type: '@@router/LOCATION_CHANGE', payload: { location, action } });
});

// Best-effort fallback when the tab closes outside a controlled context-switch reload.
const _handleUnload = () => {
  persistSessionState(store.getState());
};

window.addEventListener('beforeunload', _handleUnload);
// authentication channel requires store data to trasnfer ui state from parent window to child window
// in order to access state information store is set here
authenticationChannel.setStore(store);
// Fixed-name cmx-session BroadcastChannel for CSRF + renew (not auth-channel family).
initClientSessionCsrf();
// The org-switch broadcast must reach every tab of this browsing session, not just
// tabs that share this tab's sessionStorage-derived AuthenticationChannel name — so
// it rides the fixed-name cmx-session channel instead. A hard navigation is required:
// SPA routing to /cmx/ would not remount CMx or re-run bootstrap.
setSessionChannelHandlers({
  onOrgContextSwitched: () => {
    window.location.href = '/cmx/';
  }
});

export const tokenRequestListener = new TokenRequestChannel();
tokenRequestListener.setStore(store);

(async function() {
  // special case to dispatch the auth token
  if (initialState.login.auth) {
    store.dispatch({
      type: Auth.TOKEN_AVAILABLE,
      payload: initialState.login.auth
    });
    setIsActive(true);
    tokenRequestListener.startListening();
    // window.open copies sessionStorage, so a child can restore auth before its
    // channel bootstrap supplies contextId. Re-sync once bootstrap completes.
    await syncChildSessionHeartbeatSchedulerAfterAuth(
      authenticationChannel,
      store.dispatch
    );
  } else {
    try {
      await authenticationChannel.ready;
      // Child windows authenticate through the channel bootstrap, which calls
      // authenticationContext.setAuthorization directly and never dispatches
      // Auth.TOKEN_AVAILABLE or UserContext.CHOOSE_CONTEXT — the only actions that
      // start the scheduler. Without this the child receives peer heartbeats but
      // never posts its own.
      syncSessionHeartbeatScheduler(store.dispatch);
    } catch (e) {
      console.error(e);
    }
  }

  if (engine) {
    try {
      // if the document is in a reload situation,
      // then we want to rehydrate the store from the
      // storage engine.
      if (navigation.isReload(window)) {
        const load = storage.createLoader(engine);
        await load(store);
      } else {
        engine.clear();
      }
    } catch (e) {
      console.error(e);
    }
  }
})();

export default store;
